import type {
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeCheckNotificationItem,
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
} from 'components/TableLikeTree/context';
import type { ITableLikeTreeFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderObjectSelectionData, WorkOrderSeasonsInput } from 'gql/graphql';
import { ProjectMethodologyEnum, ContractObjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { getParamsValuesKey, usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { useUerContractSeasons } from 'pages/Project/Common/Contract/hooks/useUerContractSeasons';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';
import { useIncludedCountByConstructionType } from 'pages/Project/Common/WorkOrderStructure/hooks/useIncludedCountByConstructionType';
import type {
    ICRUDConfig,
    IUseWorkOrderStructureDataSourceProps,
    IUseWorkOrderStructureDataSourceResult,
    TWorkOrderStructureItem,
} from 'pages/Project/Common/WorkOrderStructure/types';
import {
    deleteTitle,
    doNotDeleteTitle,
    WorkOrderStructureOperation,
    WorkOrderStructureOperationType,
} from 'pages/Project/Common/WorkOrderStructure/types';
import { methodologyMap } from 'types/methodology';
import { Badge } from 'uikit';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConstructionTypes } from '../../hooks/useConstructionTypes';
import { useSeasons } from '../../hooks/useSeasons';

import { useIncludeExcludeUerWorkOrderObjectsData } from './useIncludeExcludeUerWorkOrderObjectsData';
import { useUerUpdateWorkOrderSeasons } from './useUerUpdateWorkOrderSeasons';
import { useUerWorkOrderObjectSelectionList } from './useUerWorkOrderObjectSelectionList';

export const useGetUerWorkOrderStructureDataSource = (
    props: IUseWorkOrderStructureDataSourceProps<WorkOrderObjectSelectionData>,
): IUseWorkOrderStructureDataSourceResult<WorkOrderObjectSelectionData> => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const {
        projectData,
        methodology,
        workOrderOrChangeId,
        useGetIncludedCountData,
        useIncludeExcludeWorkOrderObjects,
        isReadOnly,
        isTopFilterReadOnly,
        status,
        canEditObjectCode,
    } = props;

    const [unsortedConstructionTypes, constructionTypesStatus] = useContractConstructionTypes(
        projectData?.contractId,
        ProjectMethodologyEnum.Uer,
        status === WorkOrderStatusEnum.OnFormation,
    );

    const [workOrderConstructionType, workOrderConstructionTypesState] = useConstructionTypes(
        workOrderOrChangeId,
        status !== WorkOrderStatusEnum.OnFormation,
    );

    const [constructionTypeId, setConstructionTypeId] = useState<number>();
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [selectedSeasonsIds] = useState<number[]>([]);

    const [workOrderSeasons, workOrderSeasonsStatus] = useSeasons(workOrderOrChangeId as number);

    const [contractSeasons, contractSeasonsStatus] = useUerContractSeasons(
        projectData?.contractId,
        status === WorkOrderStatusEnum.OnFormation,
    );

    const seasons = useMemo(
        () => (status === WorkOrderStatusEnum.OnFormation ? contractSeasons : workOrderSeasons),
        [contractSeasons, status, workOrderSeasons],
    );
    const seasonsStatus = useMemo(
        () => (status === WorkOrderStatusEnum.OnFormation ? contractSeasonsStatus : workOrderSeasonsStatus),
        [contractSeasonsStatus, status, workOrderSeasonsStatus],
    );

    const { uerWorkOrderObjectSelectionListMakeRequest, uerWorkOrderObjectSelectionListData } =
        useUerWorkOrderObjectSelectionList();

    const { uerUpdateWorkOrderSeasonsStatus, uerUpdateWorkOrderSeasonsMakeRequest } =
        useUerUpdateWorkOrderSeasons(workOrderOrChangeId);

    const tablePageKey = getParamsValuesKey(pathname)?.replace('_structure', '');

    const { savedFilterState, handleSaveFilterState, handleCleanEntityMemoizedValues } = usePageFilterSaver(
        useMemo(
            () => ({
                withFakeSetter: true,
                customSaveKey: tablePageKey,
            }),
            [tablePageKey],
        ),
    );

    const areCheckboxesDisabled = useMemo(() => false, []);

    const items = useMemo(
        () => uerWorkOrderObjectSelectionListData?.uerWorkOrderObjectSelectionList?.items?.filter?.((i) => i),
        [uerWorkOrderObjectSelectionListData?.uerWorkOrderObjectSelectionList?.items],
    );

    const { includedCount, updateIncludedCount } = useIncludedCountByConstructionType({
        workOrderId: workOrderOrChangeId,
        useGetIncludedCountData,
    });

    useEffect(() => {
        /* после исключения всех объектов из вида строительства очищаем сохраненные фильтры для него
          т.к этот вид строительства пропадает со страницы нз и восстановить данные корректно нельзя */
        const currentWorkOrderConstructionTypeId = savedFilterState?.constructionTypeId as TConstructionTypesIndexes;

        if (tablePageKey && includedCount && !includedCount?.[currentWorkOrderConstructionTypeId]) {
            handleCleanEntityMemoizedValues({
                pathNameKey: tablePageKey,
            });
        }
    }, [handleCleanEntityMemoizedValues, includedCount, savedFilterState?.constructionTypeId, tablePageKey]);

    const actualConstructionTypes = useMemo(() => {
        if (!status) {
            return [];
        }

        const constTypes =
            status === WorkOrderStatusEnum.OnFormation
                ? unsortedConstructionTypes || []
                : workOrderConstructionType || [];

        return [...constTypes].sort((a, b) => a?.name?.localeCompare(b?.name || ''));
    }, [status, unsortedConstructionTypes, workOrderConstructionType]);

    const { includeExcludeStatus, makeIncludeExcludeRequest } = useIncludeExcludeWorkOrderObjects({
        workOrderId: workOrderOrChangeId,
        constructionTypeId,
        checkedIds,
        setCheckedIds,
        updateIncludedCount,
        useIncludeExcludeWorkOrderObjectsData: useIncludeExcludeUerWorkOrderObjectsData,
    });

    const isLoading = useMemo(
        () =>
            constructionTypesStatus === RequestStatus.FETCHING ||
            seasonsStatus === RequestStatus.FETCHING ||
            workOrderConstructionTypesState === RequestStatus.FETCHING,
        [constructionTypesStatus, seasonsStatus, workOrderConstructionTypesState],
    );

    const disabledCheckBoxesItemsIds = useMemo(() => {
        if (includeExcludeStatus === RequestStatus.FETCHING) {
            return items?.map((i) => i.id);
        }

        return items
            ?.filter((i) => {
                if (status === WorkOrderStatusEnum.InProgress) {
                    return (i.isIncluded && !i.isActKC2Object) || (!i.fromWorkOrderVOR && i.hasActsKC2);
                }

                return false;
            })
            .map((i) => i.id);
    }, [includeExcludeStatus, items, status]);

    const checkBoxesNotifications = useMemo(
        () =>
            items?.reduce((acc: ITableLikeTreeCheckNotificationItem[], item) => {
                if (status === WorkOrderStatusEnum.InProgress) {
                    if (!item.parentId && !item.fromWorkOrderVOR && item.hasActsKC2) {
                        acc.push({
                            treeItemId: item.id,
                            message: `В составе объекта содержатся Акты КС-2${
                                item.hasPricesOther ? ' и прочие затраты' : ''
                            }. Исключение невозможно`,
                        });
                    }
                }

                return acc;
            }, []),
        [items, status],
    );

    const checkConfirmations: ITableLikeTreeCheckConfirmation[] = useMemo(
        () =>
            items
                ?.filter((i) => {
                    if (status === WorkOrderStatusEnum.InProgress) {
                        return i.isIncluded && i.isActKC2Object && (i.hasPrices || i.hasPricesOther);
                    }

                    if (status === WorkOrderStatusEnum.OnFormation) {
                        return i.isIncluded && (i.hasPrices || i.hasPricesOther);
                    }

                    return false;
                })
                ?.map((i) => ({
                    treeItemId: i.id,
                    onUncheck: {
                        message: `В составе данного ${
                            i.parentId ? 'подобъекта' : 'объекта'
                        } содержатся расценки. Вы уверены, что хотите исключить ${
                            i.parentId ? 'подобъект' : 'объект'
                        } из НЗ?`,
                        confirmLabel: 'Исключить',
                    },
                })) || [],
        [items, status],
    );

    const dragOperationDisabledItemsIds = useMemo(() => [], []);

    const [firstYear, setFirstYear] = useState<number>();
    const [lastYear, setLastYear] = useState<number>();

    useEffect(() => {
        const years = workOrderSeasons.map((s) => s.year as number);
        const fYear = Math.min(...years);
        const lYear = Math.max(...years);

        setFirstYear(fYear);
        setLastYear(lYear);
    }, [workOrderSeasons]);

    const isTopFilterDisabled = useMemo(
        () => uerUpdateWorkOrderSeasonsStatus === RequestStatus.FETCHING || seasonsStatus === RequestStatus.FETCHING,
        [seasonsStatus, uerUpdateWorkOrderSeasonsStatus],
    );

    const topFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: seasons.map((s) => {
                const isFirstActive = firstYear === s?.year;
                const isLastActive = lastYear === s?.year;

                return { id: s?.id, title: s?.year, isFirstActive, isLastActive };
            }),
            title: 'Выбранные сезоны:',
            isReadOnly: isTopFilterReadOnly,
            onFilterItemCheckChange: (seasonValues: any) => {
                const fYear = seasonValues?.firstSeason?.title;
                const isLYearLessThanFirst = seasonValues?.lastSeason?.title < seasonValues?.firstSeason?.title;
                const lYear = isLYearLessThanFirst ? seasonValues?.firstSeason?.title : seasonValues?.lastSeason?.title;

                setFirstYear(fYear);
                setLastYear(lYear);

                const input: WorkOrderSeasonsInput = {
                    firstContractSeasonId: seasonValues?.firstSeason?.id,
                    lastContractSeasonId: isLYearLessThanFirst
                        ? seasonValues?.firstSeason?.id
                        : seasonValues?.lastSeason?.id,
                };

                uerUpdateWorkOrderSeasonsMakeRequest(input);
            },
            disabled: isTopFilterDisabled,
            isFictiveFilter: true,
            secondListItems: (seasons as any)
                .filter((s: any) => !firstYear || s.year >= firstYear)
                .map((s: any) => ({
                    id: s?.id,
                    title: s?.year,
                    isLastActive: lastYear === s?.year,
                })),
        }),
        [firstYear, isTopFilterDisabled, isTopFilterReadOnly, lastYear, seasons, uerUpdateWorkOrderSeasonsMakeRequest],
    );

    const informers: ITableLikeTreeItemNotification[] = useMemo(
        () =>
            items?.reduce((acc: ITableLikeTreeItemNotification[], item) => {
                const itemsToAdd = [
                    ...(item.status === ContractObjectStatusEnum.NewRecord
                        ? [{ icon: <Badge minified size="s" status="success" /> }]
                        : []),
                    ...(item.hasPrices
                        ? [
                              {
                                  message: `В составе ${item?.parentId ? 'подобъекта' : 'объекта'} имеются расценки`,
                                  icon: <Badge size="s" status="system" label="Р" />,
                              },
                          ]
                        : []),
                ];

                if (itemsToAdd.length) {
                    acc.push({
                        items: itemsToAdd,
                        treeItemId: item.id,
                    });
                }

                return acc;
            }, []) || [],
        [items],
    );

    const refreshByConstructionType = useCallback(() => {
        if (workOrderOrChangeId && constructionTypeId) {
            uerWorkOrderObjectSelectionListMakeRequest(workOrderOrChangeId, constructionTypeId, isReadOnly);
        }
    }, [constructionTypeId, uerWorkOrderObjectSelectionListMakeRequest, workOrderOrChangeId, isReadOnly]);

    const refreshTableFilterOnDelete = useCallback(
        (removedObjectId: number) => {
            if (!constructionTypeId) {
                return;
            }
            const item = items?.find((i) => i.id === removedObjectId);
            const filters = savedFilterState?.[constructionTypeId];

            if (filters) {
                const { chapter, objectId, subObjectId } = filters;

                if (item?.workOrderObjectId === objectId) {
                    handleSaveFilterState({
                        key: String(constructionTypeId),
                        newState: { chapter },
                    });

                    return;
                }

                if (item?.workOrderObjectId === subObjectId) {
                    handleSaveFilterState({
                        key: String(constructionTypeId),
                        newState: { chapter, objectId },
                    });
                }
            }
        },
        [constructionTypeId, handleSaveFilterState, items, savedFilterState],
    );

    const crudMenuConfig: ICRUDConfig = useMemo(() => {
        const checkVisibleCallback = (treeItem: ITableLikeTreeItem) => {
            const item = items?.find((i) => i.id === treeItem.id);

            if (status === WorkOrderStatusEnum.InProgress) {
                return item?.status === ContractObjectStatusEnum.NewRecord;
            }

            return true;
        };

        return {
            isAllMenuDisabledCallback: (treeItem: ITableLikeTreeItem) => {
                const item = items?.find((i) => i.id === treeItem.id);

                if (status === WorkOrderStatusEnum.InProgress) {
                    return item?.parentId && item?.hasPrices;
                }

                return false;
            },
            create: {
                operationSuccessCallback: () => {
                    if (constructionTypeId) {
                        updateIncludedCount(constructionTypeId, 1);
                    }

                    refreshByConstructionType();
                },
                checkDisabledCallback: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => {
                    const item = items?.find((i) => i.id === treeItem.id);

                    if (status === WorkOrderStatusEnum.InProgress) {
                        return !(isChecked && !item?.hasPrices && !item?.isInUse);
                    }

                    if (status === WorkOrderStatusEnum.OnFormation) {
                        return !item?.parentId ? item?.isInUse : !(!item?.hasPrices && !item?.isInUse);
                    }

                    return false;
                },
            },
            update: {
                operationSuccessCallback: refreshByConstructionType,
                checkVisibleCallback,
                checkDisabledCallback: (treeItem: ITableLikeTreeItem) => {
                    const item = items?.find((i) => i.id === treeItem.id);

                    if (!item) {
                        return true;
                    }

                    if (status === WorkOrderStatusEnum.InProgress) {
                        return !(
                            item?.status === ContractObjectStatusEnum.NewRecord &&
                            !item?.isInUse &&
                            item?.isActKC2Object
                        );
                    }

                    if (status === WorkOrderStatusEnum.OnFormation) {
                        if (item.status !== ContractObjectStatusEnum.NewRecord) {
                            return !canEditObjectCode;
                        }

                        return !(
                            item?.status === ContractObjectStatusEnum.NewRecord &&
                            !item?.fromWorkOrderVOR &&
                            !item?.isInUse
                        );
                    }

                    return false;
                },
            },
            delete: {
                operationSuccessCallback: (object: any) => {
                    refreshByConstructionType();
                    refreshTableFilterOnDelete(object?.id);
                },
                checkVisibleCallback,
                checkDisabledCallback: (treeItem: ITableLikeTreeItem) => {
                    const item = items?.find((i) => i.id === treeItem.id);

                    if (status === WorkOrderStatusEnum.InProgress) {
                        return !(
                            !(item?.isIncluded && !item?.isActKC2Object) &&
                            !(!item?.fromWorkOrderVOR && item?.hasActsKC2) &&
                            item?.status === ContractObjectStatusEnum.NewRecord &&
                            !item?.isInUse &&
                            !item?.fromWorkOrderVOR
                        );
                    }

                    if (status === WorkOrderStatusEnum.OnFormation) {
                        return !(
                            item?.status === ContractObjectStatusEnum.NewRecord &&
                            !item?.isActKC2Object &&
                            !item?.isInUse
                        );
                    }

                    return false;
                },
            },
        };
    }, [
        canEditObjectCode,
        constructionTypeId,
        items,
        refreshByConstructionType,
        refreshTableFilterOnDelete,
        status,
        updateIncludedCount,
    ]);

    const onItemCheckChange = useCallback(
        (item: ITableLikeTreeItem, isChecked: boolean) => {
            makeIncludeExcludeRequest?.(
                isChecked ? WorkOrderStructureOperationType.Include : WorkOrderStructureOperationType.Exclude,
                item.id,
            );
        },
        [makeIncludeExcludeRequest],
    );

    const complete = useCallback(() => {
        if (methodology) {
            navigate(`/projects/${methodologyMap[methodology]}/${projectData?.uniqueId}/order/${workOrderOrChangeId}`);
        }
    }, [methodology, navigate, projectData?.uniqueId, workOrderOrChangeId]);

    const getStructureOperationConfirmationMessage = useCallback(
        (
            operation?: WorkOrderStructureOperation,
            structureItem?: TWorkOrderStructureItem<WorkOrderObjectSelectionData>,
        ) => {
            if (
                [WorkOrderStructureOperation.DeleteObject, WorkOrderStructureOperation.DeleteSubObject].includes(
                    operation as WorkOrderStructureOperation,
                )
            ) {
                const item = items?.find((i) => i.id === structureItem?.id);

                if (item?.hasPrices || item?.hasPricesOther) {
                    const objectName = item?.parentId ? 'подобъект' : 'объект';

                    return {
                        textConfirmation: `В составе ${objectName}а содержатся расценки. Вы уверены, что хотите удалить ${objectName}? Все дочерние элементы будут удалены.`,
                        textHeader: `Удаление ${objectName}а`,
                        cancelButtonText: doNotDeleteTitle,
                        confirmButtonText: deleteTitle,
                    };
                }
            }

            return undefined;
        },
        [items],
    );

    useEffect(() => {
        if (actualConstructionTypes?.length && !constructionTypeId) {
            setConstructionTypeId(actualConstructionTypes[0].id);
        }
    }, [actualConstructionTypes, constructionTypeId]);

    useEffect(() => {
        refreshByConstructionType();
    }, [refreshByConstructionType]);

    useEffect(() => {
        if (items?.length) {
            const checkedItems = items.filter((i) => i.isIncluded).map((i) => i.id);

            setCheckedIds(checkedItems);
        }
    }, [items, constructionTypeId]);

    return {
        constructionTypes: actualConstructionTypes,
        setConstructionTypeId,
        seasons,
        items,
        isLoading,
        checkedIds,
        onItemCheckChange,
        areCheckboxesDisabled,
        complete,
        isCompletePerforming: false,
        topFilterConfig,
        disabledCheckBoxesItemsIds,
        dragOperationDisabledItemsIds,
        informers,
        selectedSeasonsIds,
        crudMenuConfig,
        constructionTypeId,
        includedCount,
        isHeaderCheckboxDisabled: true,
        checkBoxesNotifications,
        checkConfirmations,
        getStructureOperationConfirmationMessage,
    };
};
